import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { translations } from '../i18n/translations';
import { navServicesLabels } from '../i18n/navServicesLabels';
import { wsjPedidosPageByLang } from '../i18n/wsjPedidosPage';
import { webDevPageByLang } from '../i18n/webDevPage';
import { ecommercePageByLang } from '../i18n/ecommercePage';
import { marketingDigitalPageByLang } from '../i18n/marketingDigitalPage';
import { mobileAppsPageByLang } from '../i18n/mobileAppsPage';
import { automationPageByLang } from '../i18n/automationPage';
import { itConsultingPageByLang } from '../i18n/itConsultingPage';
import { urbanTransportPageByLang } from '../i18n/urbanTransportPage';
import { digitalInvitesPageByLang } from '../i18n/digitalInvitesPage';
import { ambassadorsPageByLang } from '../i18n/ambassadorsPage';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') return 'pt-BR';

    // Check localStorage first
    const savedLang = localStorage.getItem('wsj-language');
    if (savedLang && translations[savedLang]) {
      return savedLang;
    }
    
    // Check browser language
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang.startsWith('pt-BR')) return 'pt-BR';
    if (browserLang.startsWith('pt')) return 'pt-PT';
    if (browserLang.startsWith('es')) return 'es';
    if (browserLang.startsWith('fr')) return 'fr';
    if (browserLang.startsWith('it')) return 'it';
    if (browserLang.startsWith('hi')) return 'hi';
    if (browserLang.startsWith('zh-TW') || browserLang === 'zh-Hant') return 'zh-TW';
    if (browserLang.startsWith('zh-CN') || browserLang === 'zh-Hans') return 'zh-CN';
    if (browserLang.startsWith('zh')) return 'zh-CN';
    if (browserLang.startsWith('de')) return 'de';
    if (browserLang.startsWith('nl')) return 'nl';
    if (browserLang.startsWith('ru')) return 'ru';
    if (browserLang.startsWith('ar')) return 'ar';
    if (browserLang.startsWith('en')) return 'en';
    
    // Default to Portuguese BR
    return 'pt-BR';
  });

  useEffect(() => {
    localStorage.setItem('wsj-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const shortLang = language?.split('-')[0];
  const baseT =
    translations[language] || (shortLang ? translations[shortLang] : null) || translations['pt-BR'];

  const t = useMemo(
    () => ({
      ...baseT,
      servicesNav:
        navServicesLabels[language] ||
        (shortLang ? navServicesLabels[shortLang] : null) ||
        navServicesLabels['pt-BR'],
      wsjPedidosPage:
        wsjPedidosPageByLang[language] ||
        (shortLang ? wsjPedidosPageByLang[shortLang] : null) ||
        wsjPedidosPageByLang['pt-BR'],
      webDevPage:
        webDevPageByLang[language] ||
        (shortLang ? webDevPageByLang[shortLang] : null) ||
        webDevPageByLang['pt-BR'],
      ecommercePage:
        ecommercePageByLang[language] ||
        (shortLang ? ecommercePageByLang[shortLang] : null) ||
        ecommercePageByLang['pt-BR'],
      marketingDigitalPage:
        marketingDigitalPageByLang[language] ||
        (shortLang ? marketingDigitalPageByLang[shortLang] : null) ||
        marketingDigitalPageByLang['pt-BR'],
      mobileAppsPage:
        mobileAppsPageByLang[language] ||
        (shortLang ? mobileAppsPageByLang[shortLang] : null) ||
        mobileAppsPageByLang['pt-BR'],
      automationPage:
        automationPageByLang[language] ||
        (shortLang ? automationPageByLang[shortLang] : null) ||
        automationPageByLang['pt-BR'],
      itConsultingPage:
        itConsultingPageByLang[language] ||
        (shortLang ? itConsultingPageByLang[shortLang] : null) ||
        itConsultingPageByLang['pt-BR'],
      urbanTransportPage:
        urbanTransportPageByLang[language] ||
        (shortLang ? urbanTransportPageByLang[shortLang] : null) ||
        urbanTransportPageByLang['pt-BR'],
      digitalInvitesPage:
        digitalInvitesPageByLang[language] ||
        (shortLang ? digitalInvitesPageByLang[shortLang] : null) ||
        digitalInvitesPageByLang['pt-BR'],
      ambassadorsPage:
        ambassadorsPageByLang[language] ||
        (shortLang ? ambassadorsPageByLang[shortLang] : null) ||
        ambassadorsPageByLang['pt-BR']
    }),
    [baseT, language, shortLang]
  );

  const changeLanguage = (newLang) => {
    if (translations[newLang]) {
      setLanguage(newLang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
